import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import {
  Repeat, Plus, MapPin, Package, Calendar, Loader2, Search,
  MessageCircle, Trash2, Eye, ChevronDown, ChevronUp,
  HandHeart, AlertCircle, X, CheckCircle2, Clock, Truck
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";

const TYPE_CONFIG = {
  disponible: { label: "Disponible", bg: "bg-green-500/10", text: "text-green-400" },
  recherche: { label: "Recherché", bg: "bg-amber-500/10", text: "text-amber-400" },
};

const STATUS_CONFIG = {
  pending_validation: { label: "En attente CMES", icon: Clock, color: "text-amber-400", bg: "bg-amber-500/10" },
  validated: { label: "Validé", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
  in_logistics: { label: "Logistique en cours", icon: Truck, color: "text-sky-600", bg: "bg-sky-50" },
  completed: { label: "Terminé", icon: CheckCircle2, color: "text-slate-600", bg: "bg-white/5" },
  rejected: { label: "Rejeté", icon: X, color: "text-red-400", bg: "bg-red-500/10" },
};

const PharmExchangePage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("browse");
  const [offers, setOffers] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [myExchanges, setMyExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filterZone, setFilterZone] = useState("");
  const [filterType, setFilterType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedOffer, setExpandedOffer] = useState(null);
  const [respondingTo, setRespondingTo] = useState(null);
  const [formData, setFormData] = useState({
    product_name: "", category: "", quantity: 0, unit_value: 0,
    expiry_date: "", description: "", exchange_type: "disponible"
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [offersRes, myRes, exchangesRes] = await Promise.all([
        axios.get(`${API}/exchange/offers`, { headers }),
        axios.get(`${API}/exchange/my-offers`, { headers }),
        axios.get(`${API}/exchange/my-exchanges`, { headers }).catch(() => ({ data: [] })),
      ]);
      setOffers(offersRes.data);
      setMyOffers(myRes.data);
      setMyExchanges(exchangesRes.data);
    } catch (err) { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };

  const handlePublish = async () => {
    if (!formData.product_name || !formData.quantity) {
      toast.error("Nom du produit et quantité requis");
      return;
    }
    try {
      await axios.post(`${API}/exchange/offers`, formData, { headers });
      toast.success("Offre publiée sur le réseau !");
      setShowForm(false);
      setFormData({ product_name: "", category: "", quantity: 0, unit_value: 0, expiry_date: "", description: "", exchange_type: "disponible" });
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleRespond = async (offerId) => {
    setRespondingTo(offerId);
    try {
      const res = await axios.post(`${API}/exchange/offers/${offerId}/respond`, {}, { headers });
      toast.success(res.data.message || "Intérêt manifesté !");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setRespondingTo(null);
    }
  };

  const handleDelete = async (offerId) => {
    try {
      await axios.delete(`${API}/exchange/offers/${offerId}`, { headers });
      toast.success("Offre retirée du réseau");
      loadAll();
    } catch (err) { toast.error("Erreur"); }
  };

  const filteredOffers = offers.filter(o => {
    if (filterZone && o.zone !== filterZone) return false;
    if (filterType && o.exchange_type !== filterType) return false;
    if (searchTerm && !o.product_name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const uniqueZones = [...new Set(offers.map(o => o.zone))].filter(Boolean);

  if (loading) return <Layout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div></Layout>;

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="pharmexchange-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-white flex items-center gap-2">
              <Repeat className="w-7 h-7 text-emerald-500" />
              PharmExchange
            </h1>
            <p className="text-slate-500 text-sm mt-1">Réseau de redistribution inter-pharmacies — Anonyme & Sécurisé</p>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-offer-btn">
            <Plus className="w-4 h-4 mr-2" /> Publier une offre
          </Button>
        </div>

        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-emerald-50 to-cyan-50 border-emerald-200">
          <CardContent className="p-4 flex items-start gap-3">
            <HandHeart className="w-6 h-6 text-emerald-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-emerald-800 font-medium">Comment fonctionne l'échange ?</p>
              <div className="flex flex-wrap gap-4 mt-2">
                {[
                  { step: "1", text: "Publiez ou trouvez un produit" },
                  { step: "2", text: "Cliquez sur 'Intéressé'" },
                  { step: "3", text: "CMES valide l'échange" },
                  { step: "4", text: "Logistique organisée" },
                ].map(s => (
                  <div key={s.step} className="flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center">{s.step}</span>
                    <span className="text-xs text-emerald-700">{s.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New Offer Form */}
        {showForm && (
          <Card className="medical-card border-emerald-200" data-testid="offer-form">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white">Nouvelle offre</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Type d'offre</Label>
                  <Select value={formData.exchange_type} onValueChange={v => setFormData({ ...formData, exchange_type: v })}>
                    <SelectTrigger className="bg-slate-900" data-testid="offer-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-900">
                      <SelectItem value="disponible">Je propose (produit disponible)</SelectItem>
                      <SelectItem value="recherche">Je recherche (indisponible grossiste)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Nom du produit</Label>
                  <Input value={formData.product_name} onChange={e => setFormData({ ...formData, product_name: e.target.value })} placeholder="Ex: Paracétamol 500mg" className="bg-slate-900" data-testid="offer-product-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Quantité</Label>
                  <Input type="number" value={formData.quantity} onChange={e => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })} className="bg-slate-900" data-testid="offer-quantity" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Valeur unitaire (FCFA)</Label>
                  <Input type="number" value={formData.unit_value} onChange={e => setFormData({ ...formData, unit_value: parseFloat(e.target.value) || 0 })} className="bg-slate-900" data-testid="offer-value" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Date d'expiration</Label>
                  <Input type="date" value={formData.expiry_date} onChange={e => setFormData({ ...formData, expiry_date: e.target.value })} className="bg-slate-900" data-testid="offer-expiry" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Description</Label>
                  <Input value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Ex: Échange contre valeur équivalente en antibiotiques" className="bg-slate-900" data-testid="offer-description" />
                </div>
              </div>
              <div className="bg-amber-500/10 rounded-lg p-3">
                <p className="text-xs text-amber-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Rappel : Votre identité reste anonyme. Seuls votre commune et ville sont visibles.</p>
              </div>
              <Button onClick={handlePublish} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="publish-offer-btn">
                <Repeat className="w-4 h-4 mr-2" /> Publier sur le réseau
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-emerald-100 pb-2">
          {[
            { id: "browse", label: `Réseau (${filteredOffers.length})` },
            { id: "mine", label: `Mes offres (${myOffers.length})` },
            { id: "exchanges", label: `Mes échanges (${myExchanges.length})` },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`exchange-tab-${tab.id}`}
              className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-all
                ${activeTab === tab.id ? "bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500" : "text-slate-500 hover:text-slate-200"}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Browse Tab */}
        {activeTab === "browse" && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Rechercher un produit..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white" data-testid="search-exchange" />
              </div>
              <Select value={filterZone} onValueChange={setFilterZone}>
                <SelectTrigger className="w-48 bg-white" data-testid="filter-zone"><SelectValue placeholder="Toutes les zones" /></SelectTrigger>
                <SelectContent className="bg-slate-900">
                  <SelectItem value=" ">Toutes les zones</SelectItem>
                  {uniqueZones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                {[{ id: "", label: "Tous" }, { id: "disponible", label: "Disponibles" }, { id: "recherche", label: "Recherchés" }].map(f => (
                  <button key={f.id} onClick={() => setFilterType(f.id)} data-testid={`filter-type-${f.id || "all"}`}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filterType === f.id ? "bg-emerald-600 text-white" : "bg-white text-slate-600 border border-white/10"}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              {filteredOffers.map(offer => {
                const config = TYPE_CONFIG[offer.exchange_type] || TYPE_CONFIG.disponible;
                return (
                  <Card key={offer.id} className="medical-card hover:shadow-md transition-shadow" data-testid={`offer-card-${offer.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${config.bg} ${config.text}`}>{config.label}</span>
                            <h3 className="font-semibold text-white">{offer.product_name}</h3>
                            {offer.is_mine && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 font-medium">Mon offre</span>}
                          </div>
                          <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{offer.zone} ({offer.ville})</span>
                            <span className="flex items-center gap-1"><Package className="w-3 h-3" />{offer.quantity} unités</span>
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Exp: {offer.expiry_date || "N/A"}</span>
                            <span className="font-medium text-slate-200">{(offer.total_value || 0).toLocaleString()} FCFA</span>
                          </div>
                          {offer.description && <p className="text-sm text-slate-600 mt-2 italic">"{offer.description}"</p>}
                          {offer.responses_count > 0 && (
                            <p className="text-xs text-emerald-600 mt-1 font-medium">{offer.responses_count} pharmacie(s) intéressée(s)</p>
                          )}
                        </div>
                        {!offer.is_mine && (
                          <Button
                            onClick={() => handleRespond(offer.id)}
                            disabled={respondingTo === offer.id}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`respond-${offer.id}`}
                          >
                            {respondingTo === offer.id ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <MessageCircle className="w-3 h-3 mr-1" />
                            )}
                            Intéressé
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {filteredOffers.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Repeat className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Aucune offre disponible. Soyez le premier à publier !</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* My Offers Tab */}
        {activeTab === "mine" && (
          <div className="space-y-3">
            {myOffers.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm mb-3">Vous n'avez pas encore publié d'offre</p>
                <Button onClick={() => { setActiveTab("browse"); setShowForm(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Plus className="w-4 h-4 mr-2" /> Créer ma première offre
                </Button>
              </div>
            ) : myOffers.map(offer => (
              <Card key={offer.id} className="medical-card" data-testid={`my-offer-${offer.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white">{offer.product_name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_CONFIG[offer.exchange_type]?.bg} ${TYPE_CONFIG[offer.exchange_type]?.text}`}>
                          {TYPE_CONFIG[offer.exchange_type]?.label}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${offer.status === "active" ? "bg-green-500/10 text-green-400" : "bg-slate-100 text-slate-500"}`}>
                          {offer.status === "active" ? "Active" : offer.status === "exchanged" ? "Échangé" : "Fermée"}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{offer.quantity} unités · {(offer.total_value || 0).toLocaleString()} FCFA · Exp: {offer.expiry_date}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {offer.responses && offer.responses.length > 0 && (
                        <Button size="sm" variant="outline" onClick={() => setExpandedOffer(expandedOffer === offer.id ? null : offer.id)} className="text-emerald-600 border-emerald-200">
                          <Eye className="w-3 h-3 mr-1" /> {offer.responses.length} réponse(s)
                        </Button>
                      )}
                      {offer.status === "active" && (
                        <Button size="sm" variant="outline" onClick={() => handleDelete(offer.id)} className="text-red-500 border-red-500/20 hover:bg-red-500/100/10">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {expandedOffer === offer.id && offer.responses && (
                    <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
                      <p className="text-xs font-semibold text-slate-500">Pharmacies intéressées (anonyme) :</p>
                      {offer.responses.map((r, i) => (
                        <div key={i} className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-emerald-500" />
                            <span className="text-sm text-emerald-800">Pharmacie zone {r.zone} ({r.ville})</span>
                          </div>
                          <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                      ))}
                      {offer.matches && offer.matches.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs font-semibold text-slate-500">Statut des échanges :</p>
                          {offer.matches.map((m, i) => {
                            const st = STATUS_CONFIG[m.status] || STATUS_CONFIG.pending_validation;
                            const StIcon = st.icon;
                            return (
                              <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${st.bg}`}>
                                <StIcon className={`w-3 h-3 ${st.color}`} />
                                <span className={`text-xs font-medium ${st.color}`}>{st.label}</span>
                                <span className="text-xs text-slate-400 ml-auto">Ref: {m.id.slice(0, 8)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* My Exchanges Tab */}
        {activeTab === "exchanges" && (
          <div className="space-y-3" data-testid="exchanges-tab">
            {myExchanges.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Repeat className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Aucun échange en cours. Parcourez le réseau et cliquez "Intéressé" sur une offre.</p>
              </div>
            ) : myExchanges.map(ex => {
              const st = STATUS_CONFIG[ex.status] || STATUS_CONFIG.pending_validation;
              const StIcon = st.icon;
              return (
                <Card key={ex.id} className="medical-card" data-testid={`exchange-${ex.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-white">{ex.product_name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.bg} ${st.color} flex items-center gap-1`}>
                            <StIcon className="w-3 h-3" /> {st.label}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                            {ex.role === "offreur" ? "Vous proposez" : "Vous demandez"}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Package className="w-3 h-3" />{ex.quantity} unités</span>
                          <span className="font-medium text-slate-200">{(ex.total_value || 0).toLocaleString()} FCFA</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />Partenaire : {ex.partner_zone} ({ex.partner_ville})</span>
                        </div>
                        {ex.status === "pending_validation" && (
                          <p className="text-xs text-amber-400 mt-2">CMES examine cet échange. Vous serez notifié par SMS/WhatsApp.</p>
                        )}
                        {ex.status === "validated" && (
                          <p className="text-xs text-emerald-600 mt-2">Échange validé ! CMES va organiser la logistique de livraison.</p>
                        )}
                        {ex.status === "in_logistics" && (
                          <p className="text-xs text-sky-600 mt-2">La logistique est en cours. Restez disponible pour la livraison.</p>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">{new Date(ex.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PharmExchangePage;
